import { ViewBody } from 'components/Sidebar'
import useGetData from 'hooks/useGetData'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'components/Intl'
import useDidUpdate from 'hooks/useDidUpdate'
import usePrevious from 'hooks/usePrevious'
import InfiniteScroll from 'components/InfiniteScroll'
import PathBreadcrumb from 'components/PathBreadcrumb'
import Scrollbars from 'components/Scrollbars'
import Alert from 'components/Alert'
import DocumentTemplateItem from './DocumentTemplateItem'
import { uncontrollable } from 'uncontrollable'
import styled from 'styled-components'

const PathContainer = styled.div`
  padding: 0 10px;
  margin-top: 5px;
  margin-bottom: 10px;
`

const LIMIT = 20

type Props = {
  elementId?: number;
  documentTemplateComponent?: React.ComponentType<React.ComponentProps<typeof DocumentTemplateItem>>;
  path: any[];
  onPathChange: (path: any[]) => void;
}

const DocumentTemplatesList: React.FC<Props> = ({
  elementId,
  documentTemplateComponent: DocumentTemplateComponent = DocumentTemplateItem,
  path = [],
  onPathChange,
}) => {
  const [_fetch, setFetch] = useState(true)
  const [cursor, setCursor] = useState(undefined)
  const [sources, setSources] = useState<any[]>([])
  const [folders, setFolders] = useState<any[]>([])
  const [templates, setTemplates] = useState<any[]>([])

  const refresh = useCallback(() => {
    setCursor(undefined)
    setSources([])
    setFolders([])
    setTemplates([])
    setFetch(value => !value)
  }, [])

  const handleLoadMore = useCallback(
    () => {
      setFetch(value => !value)
    },
    []
  )

  const handleIndexClick = useCallback(
    index => {
      onPathChange(path.slice(0, index + 1))
    },
    [path, onPathChange],
  )

  const currentPath = useMemo(() => path.length > 0 ? path[path.length - 1] : undefined, [path])

  useDidUpdate(() => {
    refresh()
  }, [currentPath?.documentTemplateSourceId, currentPath?.url, elementId])

  const args = useMemo(() => {
    const args: any = {
      cursor,
      path: currentPath?.url,
      limit: LIMIT,
    }

    if (currentPath?.documentTemplateSourceId) {
      args.documentTemplateSourceId = currentPath.documentTemplateSourceId
    } else if (elementId) {
      args.documentsContainerId = elementId
    }

    return args
  }, [_fetch])

  const {
    data: TemplatesData,
    isLoading,
  }: {
    data: any;
    isLoading: boolean;
  } = useGetData('DocumentTemplates', args)

  const PrevTemplatesData = usePrevious(TemplatesData)

  useEffect(() => {
    if (
      TemplatesData &&
      TemplatesData !== PrevTemplatesData
    ) {
      setCursor(TemplatesData.Cursor)
      setSources(sources => [
        ...sources,
        ...TemplatesData.Sources,
      ])
      setFolders(folders => [
        ...folders,
        ...TemplatesData.Folders,
      ])
      setTemplates(templates => [
        ...templates,
        ...TemplatesData.Templates,
      ])
    }
  }, [TemplatesData])

  return (
    <>
      <PathContainer>
        <PathBreadcrumb
          path={path}
          onIndexClick={handleIndexClick}
        />
      </PathContainer>
      <Scrollbars>
        <InfiniteScroll
          isLoading={isLoading}
          isLoaded={!cursor}
          onLoadMore={handleLoadMore}
        >
          {
            (
              cursor === null &&
              sources.length === 0 &&
              folders.length === 0 &&
              templates.length === 0
            ) &&
              <ViewBody>
                <Alert>
                  <FormattedMessage id='DOCUMENT_TEMPLATE_SOURCES_NO_DOCUMENT_TEMPLATES' />
                </Alert>
              </ViewBody>
            }
          {sources.map((Source) =>
            <DocumentTemplateComponent
              key={Source.Id}
              type='source'
              documentTemplateSourceId={Source.Id}
              name={Source.Name}
              url={Source.Url}
              onClick={() => {
                onPathChange([
                  ...path,
                  {
                    documentTemplateSourceId: Source.Id,
                    name: Source.Name,
                  }
                ])
              }}
            />
          )}
          {folders.map((Folder, index) =>
            <DocumentTemplateComponent
              key={index}
              type='folder'
              documentTemplateSourceId={Folder.DocumentTemplateSourceId}
              identifier={Folder.Identifier}
              name={Folder.Name}
              url={Folder.Url}
              onClick={() => {
                onPathChange([
                  ...path,
                  {
                    documentTemplateSourceId: Folder.DocumentTemplateSourceId,
                    name: Folder.Name,
                    url: Folder.Path,
                  }
                ])
              }}
            />
          )}
          {templates.map((Template, index) =>
            <FormattedMessage
              key={index}
              id={Template.DisplayName.MessageId}
              values={Template.DisplayName.Values}
            >
              {formattedMessage =>
                <DocumentTemplateComponent
                  type='template'
                  documentTemplateSourceId={Template.DocumentTemplateSourceId}
                  identifier={Template.Identifier}
                  name={formattedMessage}
                  url={Template.Url}
                  extension={Template.Extension}
                />
              }
            </FormattedMessage>
          )}
        </InfiniteScroll>
      </Scrollbars>
    </>
  )
}

export default uncontrollable(
  DocumentTemplatesList,
  {
    path: 'onPathChange'
  }
)